import React, { useState } from 'react';
import {
  Badge,
  BadgePriority as BADGE_PRIORITY,
  Divider,
  Text,
  TextButton,
  TextButtonPriority,
  Accordion,
  SpinnerTypes,
} from 'wix-ui-tpa/cssVars';
import { SpinnerWrapper } from '@wix/bookings-viewer-ui';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { EditSmall as EditIcon } from '@wix/wix-ui-icons-common';
import { VideoFillXSmall as CameraSVG } from '@wix/wix-ui-icons-common/on-stage/system';
import { classes, st } from './CalendarSummary.st.css';
import { BookingDetailsDataHooks } from '../constants';
import { useCalendarActions } from '../../../Hooks/useCalendarActions';
import {
  WidgetComponents,
  WidgetElements,
} from '../../../../../utils/bi/consts';
import { useSettings } from '@wix/tpa-settings/react';
import { useSettingsParams } from '../../../Hooks/useSettingsParams';
import { AccessibilityHtmlTags } from '../../../../../utils/accessibility/constants';
import { Preference } from '../../../../../types/types';
import {
  CalendarSummaryViewModel,
  SelectedSlotDetailsViewModel,
} from '../../../ViewModel/bookingDetailsViewModel/calendarSummary/calendarSummaryViewModel';
import { DiscountInfo } from './DiscountInfo/DiscountInfo';
import { PriceWithDiscount } from './PriceWithDiscount/PriceWithDiscount';

export interface CalendarSummaryProps {
  title: string;
  isCollapsed: boolean | undefined;
  calendarSummary: CalendarSummaryViewModel;
}

const CalendarSummary: React.FC<CalendarSummaryProps> = ({
  title,
  isCollapsed,
  calendarSummary,
}) => {
  const { isViewer, isMobile } = useEnvironment();
  const { serviceName, openPreferencesModalButton, selectedSlotDetails } =
    calendarSummary;
  const settings = useSettings();
  const settingsParams = useSettingsParams();
  const { onElementClicked, openServicesPreferencesModal } =
    useCalendarActions();
  const [isAccordionCollapsed, setAccordionCollapsed] = useState(
    !isViewer ? false : isCollapsed,
  );
  const { t } = useTranslation();

  const onAccordionItemClick = (_: any, isExpanded: boolean) => {
    setAccordionCollapsed(!isExpanded);
    onElementClicked(
      WidgetComponents.BOOKING_DETAILS,
      isExpanded ? WidgetElements.EXPAND : WidgetElements.COLLAPSE,
    );
  };

  return (
    <div
      className={st(classes.root, {
        isMobile,
        alignment: settings.get(settingsParams.textAlignment),
      })}
    >
      <Text
        data-hook={BookingDetailsDataHooks.Title}
        className={classes.title}
        tagName={settings.get(settingsParams.sectionHeaderHtmlTag)}
      >
        {title}
      </Text>
      <Divider
        className={classes.divider}
        data-hook={BookingDetailsDataHooks.Divider}
      />
      <div className={classes.serviceHeaderRow}>
        <Text
          data-hook={BookingDetailsDataHooks.SLOT_NAME}
          className={classes.commonTitleStyles}
        >
          {serviceName}
        </Text>
        {openPreferencesModalButton && (
          <TextButton
            data-hook={BookingDetailsDataHooks.OPEN_PREFERENCES_MODAL_BUTTON}
            aria-label={openPreferencesModalButton.ariaLabel}
            onClick={() => openServicesPreferencesModal()}
            priority={TextButtonPriority.primary}
            className={classes.openPreferencesModalButton}
            prefixIcon={<EditIcon />}
          >
            {openPreferencesModalButton.text}
          </TextButton>
        )}
      </div>
      {!isAccordionCollapsed && (
        <div className={classes.expandedSlotDetails}>
          <SelectedSlotDetails selectedSlotDetails={selectedSlotDetails} />
        </div>
      )}
      <Accordion
        key={`details-${isAccordionCollapsed}`}
        className={classes.calendarSummaryAccordion}
        initiallyExpanded={isAccordionCollapsed ? '' : 'details'}
        onItemClick={onAccordionItemClick}
        data-hook={BookingDetailsDataHooks.SERVICE_DETAILS}
      >
        <Accordion.Item
          id="details"
          title={
            isAccordionCollapsed
              ? t('app.bookings-details.more-details.label')
              : t('app.bookings-details.less-details.label')
          }
          className={classes.commonTextStyleAccordionItem}
        >
          {null}
        </Accordion.Item>
      </Accordion>
    </div>
  );
};

interface SelectedSlotDetailsProps {
  selectedSlotDetails: SelectedSlotDetailsViewModel;
}

const SelectedSlotDetails: React.FC<SelectedSlotDetailsProps> = ({
  selectedSlotDetails,
}) => {
  const { t } = useTranslation();
  const {
    paymentDescription,
    videoConferenceBadgeText,
    locationLabel,
    staffMemberLabel,
    durationLabel,
    addOnsLabel,
    dateAndTime,
    discountName,
    priceAfterDiscount,
    showCalculatedAtCheckoutText,
    isResolvingDateTimePrice,
  } = selectedSlotDetails;

  const DateTimePriceLoader: React.FC = () => (
    <div
      data-hook={BookingDetailsDataHooks.DATE_TIME_PRICE_LOADER}
      className={classes.dateTimePriceLoader}
    >
      <SpinnerWrapper
        diameter={16}
        type={SpinnerTypes.slim}
        isCentered={false}
        aria-label={t('services-preferences-modal.loading-text')}
      />
    </div>
  );

  const PaymentDescription: React.FC = () =>
    paymentDescription &&
    priceAfterDiscount &&
    !showCalculatedAtCheckoutText ? (
      <PriceWithDiscount
        originalPrice={paymentDescription}
        priceAfterDiscount={priceAfterDiscount}
      />
    ) : (
      <Text
        data-hook={BookingDetailsDataHooks.SLOT_PLAN_TYPE}
        className={classes.commonTextStyle}
        tagName={AccessibilityHtmlTags.Paragraph}
        key="payment-description"
      >
        {paymentDescription}
      </Text>
    );

  const VideoConferenceBadge: React.FC = () => {
    return (
      <Badge
        data-hook={BookingDetailsDataHooks.VIDEO_CONFERENCE_BADGE}
        priority={BADGE_PRIORITY.light}
        icon={<CameraSVG />}
        className={classes.videoConferenceBadge}
      >
        {videoConferenceBadgeText}
      </Badge>
    );
  };
  const AddOnsLabel: React.FC = () => {
    return (
      <Text
        data-hook={BookingDetailsDataHooks.ADDONS_LABEL}
        className={classes.commonTextStyle}
        tagName={AccessibilityHtmlTags.Paragraph}
        key="add-ons-label"
      >
        {addOnsLabel}
      </Text>
    );
  };
  const DateAndTime: React.FC = () => {
    return (
      <Text
        data-hook={BookingDetailsDataHooks.SLOT_DATE_AND_TIME}
        className={classes.commonTextStyle}
        tagName={AccessibilityHtmlTags.Paragraph}
        key="date-and-time"
      >
        {dateAndTime}
      </Text>
    );
  };
  const Location: React.FC = () => {
    return (
      <Text
        data-hook={`${BookingDetailsDataHooks.TIME_SLOT_DETAILS}-${Preference.LOCATION}`}
        className={classes.commonTextStyle}
        tagName={AccessibilityHtmlTags.Paragraph}
        key="location"
      >
        {locationLabel}
      </Text>
    );
  };
  const StaffMember: React.FC = () => {
    return (
      <Text
        data-hook={`${BookingDetailsDataHooks.TIME_SLOT_DETAILS}-${Preference.STAFF_MEMBER}`}
        className={classes.commonTextStyle}
        tagName={AccessibilityHtmlTags.Paragraph}
        key="staff"
      >
        {staffMemberLabel}
      </Text>
    );
  };
  const Duration: React.FC = () => {
    return (
      <Text
        data-hook={`${BookingDetailsDataHooks.TIME_SLOT_DETAILS}-${Preference.DURATION}`}
        className={classes.commonTextStyle}
        tagName={AccessibilityHtmlTags.Paragraph}
        key="duration"
      >
        {durationLabel}
      </Text>
    );
  };

  return (
    <div className={classes.timeSlotDetailsWrapper}>
      {videoConferenceBadgeText && <VideoConferenceBadge />}
      {isResolvingDateTimePrice && <DateTimePriceLoader />}
      {paymentDescription && <PaymentDescription />}
      {addOnsLabel && <AddOnsLabel />}
      <DiscountInfo
        discountName={discountName}
        showCalculatedAtCheckoutText={!!showCalculatedAtCheckoutText}
      />
      <DateAndTime />
      {locationLabel && <Location />}
      {staffMemberLabel && <StaffMember />}
      {durationLabel && <Duration />}
    </div>
  );
};

export default CalendarSummary;
